import React, { useState, useEffect } from 'react';
import { db, functions } from '../../services/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const GuidesCuration = () => {
  const [stats, setStats] = useState({
    totalRestaurants: 0,
    withLiveData: 0,
    withFallbackData: 0,
    withNoData: 0,
    lastRefresh: null,
    collections: {}
  });
  const [refreshing, setRefreshing] = useState(false);
  const [fixingCoordinates, setFixingCoordinates] = useState(false);
  const [refreshLog, setRefreshLog] = useState([]);
  const [coordinateLog, setCoordinateLog] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load current statistics with enhanced data type detection
  const loadStats = async () => {
    setLoading(true);
    
    const collections = [
  'restaurants - santa barbara',
  'restaurants - bay area', 
  'restaurants - carmel+monterey',
  'restaurants - new york',
  'restaurants - napa',
  'restaurants - mexico city',
  'restaurants - hawaii island',
  'restaurants - breckenridge',
  'restaurants - laguna + newport',
  'restaurants - miami',
  'restaurants - los angeles'
];
    
    const collectionStats = {};
    let totalRestaurants = 0;
    let withLiveData = 0;
    let withFallbackData = 0;
    let withNoData = 0;
    let lastRefresh = null;
    
    for (const collectionName of collections) {
      try {
        const snapshot = await getDocs(collection(db, collectionName));
        const restaurants = [];
        let collectionWithLiveData = 0;
        let collectionWithFallbackData = 0;
        let collectionWithNoData = 0;
        let collectionLastRefresh = null;
        
        snapshot.forEach(doc => {
          const data = doc.data();
          restaurants.push({
            id: doc.id,
            ...data
          });
          
          // Enhanced data type detection
          if (data.liveData) {
            if (data.dataFreshness === 'live') {
              withLiveData++;
              collectionWithLiveData++;
            } else if (data.dataFreshness === 'fallback') {
              withFallbackData++;
              collectionWithFallbackData++;
            } else {
              // Has liveData but unknown freshness - assume live
              withLiveData++;
              collectionWithLiveData++;
            }
          } else {
            withNoData++;
            collectionWithNoData++;
          }
          
          if (data.lastRefreshed) {
            const refreshDate = data.lastRefreshed.toDate();
            if (!collectionLastRefresh || refreshDate > collectionLastRefresh) {
              collectionLastRefresh = refreshDate;
            }
            if (!lastRefresh || refreshDate > lastRefresh) {
              lastRefresh = refreshDate;
            }
          }
        });
        
        collectionStats[collectionName] = {
          total: restaurants.length,
          withLiveData: collectionWithLiveData,
          withFallbackData: collectionWithFallbackData,
          withNoData: collectionWithNoData,
          lastRefresh: collectionLastRefresh,
          restaurants
        };
        
        totalRestaurants += restaurants.length;
        
      } catch (error) {
        console.error(`Error loading ${collectionName}:`, error);
        collectionStats[collectionName] = {
          total: 0,
          withLiveData: 0,
          withFallbackData: 0,
          withNoData: 0,
          lastRefresh: null,
          error: error.message
        };
      }
    }
    
    setStats({
      totalRestaurants,
      withLiveData,
      withFallbackData,
      withNoData,
      lastRefresh,
      collections: collectionStats
    });
    
    setLoading(false);
  };

  // Enhanced refresh with better progress tracking
  const handleRefreshAllData = async () => {
    setRefreshing(true);
    setRefreshLog(['🔄 Starting batch refresh of all restaurant data...']);
    
    try {
      const refreshAllData = httpsCallable(functions, 'refreshAllRestaurantData');
      
      // Add progress updates every 15 seconds
      const progressInterval = setInterval(() => {
        setRefreshLog(prev => [...prev, '⏳ Processing restaurants... (this may take several minutes)']);
      }, 15000);
      
      // Add timeout warning after 2 minutes
      const timeoutWarning = setTimeout(() => {
        setRefreshLog(prev => [...prev, '⚠️ This is taking longer than usual - please be patient...']);
      }, 120000);
      
      const result = await refreshAllData();
      
      clearInterval(progressInterval);
      clearTimeout(timeoutWarning);
      
      if (result.data.success) {
        setRefreshLog(prev => [
          ...prev,
          `🎉 BATCH REFRESH COMPLETED SUCCESSFULLY!`,
          `📊 Total Processed: ${result.data.processed} restaurants`,
          `✅ Enhanced with Live Data: ${result.data.enhanced} restaurants`,
          `📈 Success Rate: ${Math.round((result.data.enhanced / result.data.processed) * 100)}%`,
          `⏰ Completed at: ${new Date(result.data.timestamp).toLocaleString()}`,
          `🔄 All restaurant data has been refreshed - maps should now show updated information!`
        ]);
        
        // Reload stats to show updated numbers
        setTimeout(async () => {
          setRefreshLog(prev => [...prev, '📊 Updating statistics display...']);
          await loadStats();
          setRefreshLog(prev => [...prev, '✅ Statistics updated - refresh complete!']);
        }, 2000);
        
      } else {
        setRefreshLog(prev => [...prev, '❌ Batch refresh failed - please try again']);
      }
      
    } catch (error) {
      setRefreshLog(prev => [
        ...prev,
        `❌ Error during batch refresh: ${error.message}`,
        'Please check your admin permissions and try again.',
        'If this persists, check Firebase Functions logs for detailed error information.'
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  // NEW: Fix coordinates for all restaurants (batch processing)
  const handleFixCoordinates = async () => {
    setFixingCoordinates(true);
    setCoordinateLog(['🗺️ Starting coordinate fixing for all restaurants...']);
    
    try {
      const fixCoordinates = httpsCallable(functions, 'fixRestaurantCoordinatesApple');
      
      let currentCollectionIndex = 0;
      let allProcessed = 0;
      let allFixed = 0;
      let isCompleted = false;
      
      // Process collections one by one
      while (!isCompleted) {
        setCoordinateLog(prev => [...prev, `🔄 Processing collection ${currentCollectionIndex + 1}/11...`]);
        
        try {
          const result = await fixCoordinates({ collectionIndex: currentCollectionIndex });
          
          if (result.data.success) {
            allProcessed += result.data.totalProcessed;
            allFixed += result.data.totalFixed;
            
            setCoordinateLog(prev => [
              ...prev,
              `✅ ${result.data.collectionName}: ${result.data.totalFixed}/${result.data.totalProcessed} coordinates fixed`
            ]);
            
            isCompleted = result.data.completed;
            currentCollectionIndex = result.data.nextCollectionIndex;
            
            // Short delay between collections
            if (!isCompleted) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          } else {
            setCoordinateLog(prev => [...prev, `❌ Failed to process collection ${currentCollectionIndex + 1}`]);
            break;
          }
        } catch (collectionError) {
          setCoordinateLog(prev => [
            ...prev,
            `❌ Error processing collection ${currentCollectionIndex + 1}: ${collectionError.message}`
          ]);
          break;
        }
      }
      
      if (isCompleted) {
        setCoordinateLog(prev => [
          ...prev,
          `🎉 COORDINATE FIXING COMPLETED SUCCESSFULLY!`,
          `📊 Total Processed: ${allProcessed} restaurants`,
          `✅ Coordinates Fixed: ${allFixed} restaurants`,
          `📈 Success Rate: ${allProcessed > 0 ? Math.round((allFixed / allProcessed) * 100) : 0}%`,
          `⏰ Completed at: ${new Date().toLocaleString()}`,
          `🗺️ All restaurant coordinates have been fixed using Apple Maps - pins should now be perfectly positioned!`
        ]);
        
        // Reload stats to show any changes
        setTimeout(async () => {
          setCoordinateLog(prev => [...prev, '📊 Updating statistics display...']);
          await loadStats();
          setCoordinateLog(prev => [...prev, '✅ Statistics updated - coordinate fixing complete!']);
        }, 2000);
      }
      
    } catch (error) {
      setCoordinateLog(prev => [
        ...prev,
        `❌ Error during coordinate fixing: ${error.message}`,
        'Please check your admin permissions and try again.',
        'If this persists, check Firebase Functions logs for detailed error information.'
      ]);
    } finally {
      setFixingCoordinates(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const formatDate = (date) => {
    if (!date) return 'Never';
    return date.toLocaleString();
  };

  const getRefreshStatus = (lastRefresh) => {
    if (!lastRefresh) return 'Never refreshed';
    
    const now = new Date();
    const diffHours = (now - lastRefresh) / (1000 * 60 * 60);
    
    if (diffHours < 1) return `${Math.round(diffHours * 60)} minutes ago`;
    if (diffHours < 24) return `${Math.round(diffHours)} hours ago`;
    if (diffHours < 168) return `${Math.round(diffHours / 24)} days ago`;
    return `${Math.round(diffHours / 168)} weeks ago`;
  };

  const getTotalWithData = () => {
    return stats.withLiveData + stats.withFallbackData;
  };

  return (
    <div style={{ padding: '1.36rem', maxWidth: '816px', margin: '0 auto' }}>
      <div style={{ marginBottom: '1.36rem' }}>
        <h1 style={{ margin: '0 0 0.34rem 0', fontSize: '1.36rem', fontWeight: '600' }}>
          Guides Curation
        </h1>
        <p style={{ margin: 0, color: '#666', fontSize: '0.748rem' }}>
          Manage restaurant live data and batch processing across all cities
        </p>
      </div>

      {/* Enhanced Overall Statistics */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '1.36rem',
        borderRadius: '8.16px',
        marginBottom: '1.36rem'
      }}>
        <h2 style={{ margin: '0 0 0.68rem 0', fontSize: '1.02rem' }}>
          Overall Statistics
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(122.4px, 1fr))', gap: '0.68rem' }}>
          <div>
            <div style={{ fontSize: '1.36rem', fontWeight: 'bold' }}>{stats.totalRestaurants}</div>
            <div style={{ opacity: 0.9 }}>Total Restaurants</div>
          </div>
          <div>
            <div style={{ fontSize: '1.36rem', fontWeight: 'bold', color: '#4ade80' }}>{getTotalWithData()}</div>
            <div style={{ opacity: 0.9 }}>With Any Data</div>
          </div>
          <div>
            <div style={{ fontSize: '1.36rem', fontWeight: 'bold', color: '#22c55e' }}>{stats.withLiveData}</div>
            <div style={{ opacity: 0.9 }}>Live Google Data</div>
          </div>
          <div>
            <div style={{ fontSize: '1.36rem', fontWeight: 'bold', color: '#fbbf24' }}>{stats.withFallbackData}</div>
            <div style={{ opacity: 0.9 }}>Fallback Data</div>
          </div>
          <div>
            <div style={{ fontSize: '1.36rem', fontWeight: 'bold', color: '#f87171' }}>{stats.withNoData}</div>
            <div style={{ opacity: 0.9 }}>No Data</div>
          </div>
          <div>
            <div style={{ fontSize: '0.816rem', fontWeight: 'bold' }}>
              {getRefreshStatus(stats.lastRefresh)}
            </div>
            <div style={{ opacity: 0.9 }}>Last Global Refresh</div>
          </div>
        </div>
        
        {/* Coverage percentage */}
        <div style={{ marginTop: '0.68rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.02rem', fontWeight: 'bold' }}>
            {stats.totalRestaurants > 0 ? Math.round((getTotalWithData() / stats.totalRestaurants) * 100) : 0}% Coverage
          </div>
          <div style={{ opacity: 0.9, fontSize: '0.612rem' }}>
            Restaurants with complete or fallback data
          </div>
        </div>
      </div>

      {/* Enhanced Batch Refresh Section */}
      <div style={{
        background: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: '5.44px',
        padding: '1.36rem',
        marginBottom: '1.36rem'
      }}>
        <h2 style={{ margin: '0 0 0.68rem 0', fontSize: '0.884rem' }}>
          Data Management
        </h2>
        <p style={{ margin: '0 0 1.02rem 0', color: '#666' }}>
          Refresh live data for all restaurants across all cities. This process takes 2-5 minutes and should be run weekly. 
          Watch progress in the terminal with: <code style={{ background: '#f5f5f5', padding: '0.136rem 0.272rem', borderRadius: '2.04px' }}>firebase functions:log</code>
        </p>
        
        <div style={{ display: 'flex', gap: '0.68rem', flexWrap: 'wrap' }}>
          <button
            onClick={handleRefreshAllData}
            disabled={refreshing || fixingCoordinates}
            style={{
              padding: '0.68rem 1.36rem',
              background: refreshing ? '#ccc' : '#007AFF',
              color: 'white',
              border: 'none',
              borderRadius: '5.44px',
              fontSize: '0.68rem',
              fontWeight: '600',
              cursor: (refreshing || fixingCoordinates) ? 'not-allowed' : 'pointer'
            }}
          >
            {refreshing ? '🔄 Refreshing All Data...' : '🔄 Refresh All Restaurant Data'}
          </button>

          <button
            onClick={handleFixCoordinates}
            disabled={refreshing || fixingCoordinates}
            style={{
              padding: '0.68rem 1.36rem',
              background: fixingCoordinates ? '#ccc' : '#FF6B35',
              color: 'white',
              border: 'none',
              borderRadius: '5.44px',
              fontSize: '0.68rem',
              fontWeight: '600',
              cursor: (refreshing || fixingCoordinates) ? 'not-allowed' : 'pointer'
            }}
          >
            {fixingCoordinates ? '🗺️ Fixing Coordinates...' : '🗺️ Fix All Coordinates (One-time)'}
          </button>
        </div>

        {refreshLog.length > 0 && (
          <div style={{
            background: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: '4.08px',
            padding: '0.68rem',
            maxHeight: '138.72px',
            overflowY: 'auto',
            marginTop: '0.68rem'
          }}>
            <h4 style={{ margin: '0 0 0.34rem 0', fontSize: '0.612rem', color: '#666' }}>
              Refresh Progress Log:
            </h4>
            {refreshLog.map((log, index) => (
              <div key={index} style={{ 
                fontSize: '0.578rem', 
                marginBottom: '0.17rem',
                fontFamily: 'monospace',
                color: log.includes('🎉') ? '#22c55e' : 
                      log.includes('❌') ? '#ef4444' : 
                      log.includes('⚠️') ? '#f59e0b' : 
                      '#374151'
              }}>
                {log}
              </div>
            ))}
          </div>
        )}

        {coordinateLog.length > 0 && (
          <div style={{
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            borderRadius: '4.08px',
            padding: '0.68rem',
            maxHeight: '138.72px',
            overflowY: 'auto',
            marginTop: '0.68rem'
          }}>
            <h4 style={{ margin: '0 0 0.34rem 0', fontSize: '0.612rem', color: '#666' }}>
              Coordinate Fixing Log:
            </h4>
            {coordinateLog.map((log, index) => (
              <div key={index} style={{ 
                fontSize: '0.578rem', 
                marginBottom: '0.17rem',
                fontFamily: 'monospace',
                color: log.includes('🎉') ? '#22c55e' : 
                      log.includes('❌') ? '#ef4444' : 
                      log.includes('⚠️') ? '#f59e0b' : 
                      '#374151'
              }}>
                {log}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Enhanced Collection Details */}
      <div style={{
        background: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: '5.44px',
        padding: '1.36rem'
      }}>
        <h2 style={{ margin: '0 0 1.02rem 0', fontSize: '0.884rem' }}>
          Collection Details
        </h2>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '1.36rem', color: '#666' }}>
            Loading collection statistics...
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '0.68rem' }}>
            {Object.entries(stats.collections).map(([collectionName, data]) => (
              <div key={collectionName} style={{
                border: '1px solid #f0f0f0',
                borderRadius: '4.08px',
                padding: '1.02rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.68rem' }}>
                  <h3 style={{ margin: 0, fontSize: '0.748rem', fontWeight: '600' }}>
                    {collectionName.replace('restaurants - ', '').replace(/\b\w/g, l => l.toUpperCase())}
                  </h3>
                  <div style={{ 
                    padding: '0.17rem 0.51rem', 
                    background: data.error ? '#fee' : '#efe', 
                    color: data.error ? '#c33' : '#393',
                    borderRadius: '8.16px',
                    fontSize: '0.544rem',
                    fontWeight: '600'
                  }}>
                    {data.error ? 'Error' : 'Active'}
                  </div>
                </div>
                
                {data.error ? (
                  <div style={{ color: '#c33', fontSize: '0.612rem' }}>
                    Error: {data.error}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(88.4px, 1fr))', gap: '0.68rem' }}>
                    <div>
                      <div style={{ fontSize: '1.02rem', fontWeight: 'bold', color: '#333' }}>
                        {data.total}
                      </div>
                      <div style={{ color: '#666', fontSize: '0.544rem' }}>Total</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.02rem', fontWeight: 'bold', color: '#22c55e' }}>
                        {data.withLiveData}
                      </div>
                      <div style={{ color: '#666', fontSize: '0.544rem' }}>Live Data</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.02rem', fontWeight: 'bold', color: '#fbbf24' }}>
                        {data.withFallbackData}
                      </div>
                      <div style={{ color: '#666', fontSize: '0.544rem' }}>Fallback</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.02rem', fontWeight: 'bold', color: '#f87171' }}>
                        {data.withNoData}
                      </div>
                      <div style={{ color: '#666', fontSize: '0.544rem' }}>No Data</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.02rem', fontWeight: 'bold', color: '#34C759' }}>
                        {data.total > 0 ? Math.round(((data.withLiveData + data.withFallbackData) / data.total) * 100) : 0}%
                      </div>
                      <div style={{ color: '#666', fontSize: '0.544rem' }}>Coverage</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.612rem', fontWeight: '600', color: '#666' }}>
                        {formatDate(data.lastRefresh)}
                      </div>
                      <div style={{ color: '#666', fontSize: '0.544rem' }}>Last Refresh</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Terminal Monitoring Instructions */}
      <div style={{
        background: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '5.44px',
        padding: '1.02rem',
        marginTop: '1.36rem'
      }}>
        <h3 style={{ margin: '0 0 0.68rem 0', fontSize: '0.748rem', color: '#333' }}>
          🔍 Monitor Progress in Terminal
        </h3>
        <p style={{ margin: '0 0 0.68rem 0', color: '#666', fontSize: '0.612rem' }}>
          To watch real-time progress during batch operations, open a new terminal tab and run:
        </p>
        <div style={{
          background: '#1a1a1a',
          color: '#00ff00',
          padding: '0.68rem',
          borderRadius: '4.08px',
          fontFamily: 'monospace',
          fontSize: '0.612rem'
        }}>
          cd ~/artifact-GitHub<br/>
          firebase functions:log
        </div>
        <p style={{ margin: '0.68rem 0 0 0', color: '#666', fontSize: '0.578rem' }}>
          This shows real-time Firebase Functions logs including detailed progress of each restaurant being processed.
        </p>
      </div>
    </div>
  );
};

export default GuidesCuration;